import { ShootingSession } from "../../types/ShootingSession";
import { calculateTotalScoreSum } from "../../utils/calculateScore";

export type SessionHistorySortOrder = "dateDesc" | "dateAsc" | "totalScoreDesc" | "totalScoreAsc";

export interface SessionHistoryFilters {
  shootingEventId: string;
  dateFrom: string;
  dateTo: string;
  shotCount: string;
  sortOrder: SessionHistorySortOrder;
}

export function getSessionTotalScore(session: ShootingSession): number {
  return calculateTotalScoreSum(session.targetResults.flatMap((targetResult) => targetResult.listOfPointsScored));
}

export function getTotalShotsCount(session: ShootingSession): number {
  return session.shotsPerTarget * session.targetResults.length;
}

export function getSessionTargetCount(session: ShootingSession): number {
  return session.targetResults.length;
}

export function getSessionDateInputValue(dateString: string): string {
  if (!dateString) {
    return "";
  }

  const dateOnlyMatch = dateString.match(/^\d{4}-\d{2}-\d{2}/);
  if (dateOnlyMatch) {
    return dateOnlyMatch[0];
  }

  const parsedDate = new Date(dateString);
  if (!Number.isNaN(parsedDate.getTime())) {
    const year = parsedDate.getFullYear();
    const month = `${parsedDate.getMonth() + 1}`.padStart(2, "0");
    const day = `${parsedDate.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return dateString.slice(0, 10);
}

export function compareSessionsNewestFirst(a: ShootingSession, b: ShootingSession): number {
  const sessionDateDiff = new Date(b.sessionDate).getTime() - new Date(a.sessionDate).getTime();
  if (sessionDateDiff !== 0) {
    return sessionDateDiff;
  }

  return new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime();
}

export function compareSessionsBySort(
  a: ShootingSession,
  b: ShootingSession,
  sortOrder: SessionHistorySortOrder
): number {
  if (sortOrder === "totalScoreAsc" || sortOrder === "totalScoreDesc") {
    const scoreDiff = getSessionTotalScore(a) - getSessionTotalScore(b);
    if (scoreDiff !== 0) {
      return sortOrder === "totalScoreAsc" ? scoreDiff : -scoreDiff;
    }

    return compareSessionsNewestFirst(a, b);
  }

  const sessionDateDiff = new Date(a.sessionDate).getTime() - new Date(b.sessionDate).getTime();
  if (sessionDateDiff !== 0) {
    return sortOrder === "dateAsc" ? sessionDateDiff : -sessionDateDiff;
  }

  const lastModifiedDiff = new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
  return sortOrder === "dateAsc" ? lastModifiedDiff : -lastModifiedDiff;
}

export function filterSessions(
  sessions: ShootingSession[],
  filters: SessionHistoryFilters
): ShootingSession[] {
  return sessions
    .filter((session) => {
      if (filters.shootingEventId && session.shootingEventId !== filters.shootingEventId) {
        return false;
      }

      const sessionDate = getSessionDateInputValue(session.sessionDate);
      if (filters.dateFrom && sessionDate < filters.dateFrom) {
        return false;
      }

      if (filters.dateTo && sessionDate > filters.dateTo) {
        return false;
      }

      if (filters.shotCount) {
        const expectedShotCount = Number(filters.shotCount);
        if (!Number.isInteger(expectedShotCount) || expectedShotCount < 0) {
          return false;
        }

        if (getTotalShotsCount(session) !== expectedShotCount) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => compareSessionsBySort(a, b, filters.sortOrder));
}
